import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createTournament, updateTournament, supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  capacity: number;
  registration_fee: number;
  prize_pool: number;
  description?: string;
  venue?: string;
  registration_open_date?: string | null;
  registration_close_date?: string | null;
  scoring_method?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tournament?: Tournament | null;
}

const toDatetimeLocal = (iso: string | undefined | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface FormState {
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  capacity: string;
  registration_fee: string;
  prize_pool: string;
  venue: string;
  description: string;
  registration_open_date: string;
  registration_close_date: string;
  scoring_method: string;
}

const emptyForm = (): FormState => ({
  name: '',
  start_date: '',
  end_date: '',
  status: 'upcoming',
  capacity: '250',
  registration_fee: '50000',
  prize_pool: '50000000',
  venue: '',
  description: '',
  registration_open_date: '',
  registration_close_date: '',
  scoring_method: 'stroke_play_net',
});

export default function MobileTournamentSheet({ isOpen, onClose, tournament }: Props) {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (tournament) {
        setForm({
          name: tournament.name,
          start_date: tournament.start_date,
          end_date: tournament.end_date,
          status: tournament.status,
          capacity: String(tournament.capacity),
          registration_fee: String(tournament.registration_fee),
          prize_pool: String(tournament.prize_pool),
          venue: tournament.venue || '',
          description: tournament.description || '',
          registration_open_date: toDatetimeLocal(tournament.registration_open_date),
          registration_close_date: toDatetimeLocal(tournament.registration_close_date),
          scoring_method: 'stroke_play_net',
        });
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', `scoring_method_${tournament.id}`)
          .maybeSingle()
          .then(({ data }) => {
            if (data?.value) setForm(prev => ({ ...prev, scoring_method: data.value as string }));
          });
      } else {
        setForm(emptyForm());
      }
    }
  }, [isOpen, tournament]);

  const f = (key: keyof FormState, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const saveScoringMethod = async (tournamentId: string, method: string) => {
    const key = `scoring_method_${tournamentId}`;
    const { data: existing } = await supabase
      .from('system_settings')
      .select('id')
      .eq('key', key)
      .maybeSingle();
    if (existing) {
      await supabase.from('system_settings').update({ value: method }).eq('key', key);
    } else {
      await supabase.from('system_settings').insert({
        key,
        value: method,
        category: 'tournament',
        description: 'Scoring method for tournament',
        is_public: true,
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const regFee = parseFloat(form.registration_fee);
    const prizePool = parseFloat(form.prize_pool);
    if (regFee > 999999999.99) { setError('Registration fee cannot exceed ₦999,999,999.99'); return; }
    if (prizePool > 999999999.99) { setError('Prize pool cannot exceed ₦999,999,999.99'); return; }

    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        status: form.status,
        capacity: parseInt(form.capacity) || 0,
        registration_fee: regFee,
        prize_pool: prizePool,
        venue: form.venue.trim() || undefined,
        description: form.description.trim() || undefined,
        registration_open_date: form.registration_open_date ? new Date(form.registration_open_date).toISOString() : null,
        registration_close_date: form.registration_close_date ? new Date(form.registration_close_date).toISOString() : null,
      };

      let tournamentId = tournament?.id ?? '';
      if (tournament) {
        const { error: err } = await updateTournament(tournament.id, payload);
        if (err) throw err;
        toast.success('Tournament updated');
      } else {
        const { data: created, error: err } = await createTournament(payload);
        if (err) throw err;
        tournamentId = created?.id ?? '';
        toast.success('Tournament created');
      }
      if (tournamentId) {
        await saveScoringMethod(tournamentId, form.scoring_method);
      }
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save tournament');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const field = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50';
  const label = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-x-0 top-0 bottom-20 z-40 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">{tournament ? 'Edit Tournament' : 'Create Tournament'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the tournament details</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors">
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSave} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">{error}</div>
          )}

          <div>
            <label className={label}>Tournament Name *</label>
            <input type="text" value={form.name} onChange={e => f('name', e.target.value)} required className={field} placeholder="e.g. Ibom Open 2026" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Start Date *</label>
              <input type="date" value={form.start_date} onChange={e => f('start_date', e.target.value)} required className={field} />
            </div>
            <div>
              <label className={label}>End Date *</label>
              <input type="date" value={form.end_date} onChange={e => f('end_date', e.target.value)} required className={field} />
            </div>
          </div>

          <div>
            <label className={label}>Status *</label>
            <select value={form.status} onChange={e => f('status', e.target.value)} className={field}>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className={label}>Scoring Method</label>
            <select value={form.scoring_method} onChange={e => f('scoring_method', e.target.value)} className={field}>
              <option value="stroke_play_net">Stroke Play NET</option>
              <option value="stableford_net">Stableford NET</option>
            </select>
          </div>

          <div>
            <label className={label}>Capacity *</label>
            <input type="number" value={form.capacity} onChange={e => f('capacity', e.target.value)} required min="1" className={field} />
          </div>

          <div>
            <label className={label}>Registration Fee (₦) *</label>
            <input type="number" value={form.registration_fee} onChange={e => f('registration_fee', e.target.value)} required step="0.01" max="999999999.99" className={field} />
            <p className="text-xs text-gray-400 mt-1">Maximum: ₦999,999,999.99</p>
          </div>

          <div>
            <label className={label}>Prize Pool (₦) *</label>
            <input type="number" value={form.prize_pool} onChange={e => f('prize_pool', e.target.value)} required step="0.01" max="999999999.99" className={field} />
            <p className="text-xs text-gray-400 mt-1">Maximum: ₦999,999,999.99</p>
          </div>

          <div>
            <label className={label}>Venue</label>
            <input type="text" value={form.venue} onChange={e => f('venue', e.target.value)} className={field} placeholder="e.g. Ibom Golf & Country Club" />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-bold text-golf-gray mb-3">Registration Timeline</p>
            <div className="space-y-3">
              <div>
                <label className={label}>Registration Opens</label>
                <input type="datetime-local" value={form.registration_open_date} onChange={e => f('registration_open_date', e.target.value)} className={field} />
              </div>
              <div>
                <label className={label}>Registration Closes</label>
                <input type="datetime-local" value={form.registration_close_date} onChange={e => f('registration_close_date', e.target.value)} className={field} />
              </div>
            </div>
          </div>

          <div>
            <label className={label}>Description</label>
            <textarea value={form.description} onChange={e => f('description', e.target.value)} rows={4} className={`${field} resize-none`} placeholder="Tournament description..." />
          </div>

          <div className="pb-2" />
        </form>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0">
          <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="submit" onClick={handleSave} disabled={saving} className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors">
            {saving ? 'Saving...' : tournament ? 'Save Changes' : 'Create Tournament'}
          </button>
        </div>
      </div>
    </div>
  );
}
